import { updateEstablishment, useEstablishment } from '@gozoki/api'
import type { Establishment } from '@gozoki/api-types'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import z from 'zod/v3'
import {
  EstablishmentForm,
  type EstablishmentFormValues,
} from '~/components/routes/sellers/EstablishmentForm'
import { Spinner } from '~/components/ui/spinner'
import type { PermissionTuple } from '~/utils/ability'
import { usePermissionGuard, withRoutePermissions } from '~/utils/rbac'
import { getApiErrorMessage } from '~/lib/utils'

const EDIT_ESTABLISHMENT: PermissionTuple = ['edit', 'sellers.establishments.establishment']
const READ_ESTABLISHMENT: PermissionTuple = ['read', 'sellers.establishments.establishment']

// L'écran sert aussi de fiche en lecture seule (« Voir ») : l'un OU l'autre droit l'ouvre.
export const Route = createFileRoute('/_authenticated/sellers/establishment/$establishmentId/edit')(
  {
    staticData: {
      permission: { any: [EDIT_ESTABLISHMENT, READ_ESTABLISHMENT] },
    },
    params: {
      parse: z.object({ establishmentId: z.coerce.number() }).parse,
    },
    component: EditEstablishmentPage,
  },
)
withRoutePermissions(Route)

const toFormValues = (establishment: Establishment): EstablishmentFormValues => ({
  reference: establishment.reference ?? '',
  label: establishment.label,
  address: establishment.address?.address ?? '',
  addressAdditionnal: establishment.address?.addressAdditionnal ?? '',
  postalCode: establishment.address?.postalCode ?? '',
  city: establishment.address?.city ?? '',
  sellerId: establishment.seller?.id ?? null,
})

function EditEstablishmentPage() {
  const { establishmentId } = Route.useParams()
  const navigate = useNavigate()
  const canEdit = usePermissionGuard(EDIT_ESTABLISHMENT).authorized

  const { data: establishment, isLoading } = useEstablishment(establishmentId)
  const { mutateAsync: update } = useMutation({ mutationFn: updateEstablishment })

  const navigateBack = () => navigate({ to: '/sellers/establishments' })

  if (isLoading) {
    return (
      <div className="flex justify-center p-16">
        <Spinner />
      </div>
    )
  }

  if (!establishment) {
    return <div className="text-muted-foreground p-16 text-center">Établissement introuvable.</div>
  }

  return (
    <EstablishmentForm
      key={establishment.id}
      title={canEdit ? 'Modifier un établissement' : 'Voir un établissement'}
      submitLabel="Modifier"
      canEdit={canEdit}
      defaultValues={toFormValues(establishment)}
      onCancel={navigateBack}
      onSubmit={async (values) => {
        try {
          await update({ id: establishmentId, data: values })
          navigateBack()
        } catch (error) {
          toast.error(getApiErrorMessage(error))
        }
      }}
    />
  )
}
